import {
  AwaitedNotification,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  FormSkeleton,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import {
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  useTheme
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useRetries from '../../../../hooks/useRetries'
import { getCustomerDomain, RootStoreModel } from '../../../../redux'
import { styles } from './comunicationFiles.styles'

import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import DescriptionIcon from '@material-ui/icons/Description'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { DropzoneArea } from 'material-ui-dropzone'
import { EnumEstadoFichero } from '../../../../api'
import { TemplateFileModel } from '../../../../api/Pioneer/endpoints/files/models/files'
import { DataTableErrorHandler } from '../../../../components/errors/scopes/dataTable/dataTableError'
import {
  enumKeyToValue,
  enumToSelectOptionArray,
  generateUniqueKeyWithNoPrefix
} from '../../../../helpers'
import { generateIconsFromConfiguration } from '../../../../helpers/dataTableActionIconHelper'
import { fileDownloadHelper } from '../../../../helpers/fileDownloadHelper'
import { getUserPermissions, hasPermission } from '../../../../helpers/permissionsHelper'
import { useHttpError } from '../../../../hooks/useHttpError'
import { ActionIcons, DatePeriod, GlobalNotification } from '../../../../model'
import { Pages, Resources } from '../../../../model/RBAC/constants'
import { ServiceResponse } from '../../../../model/serviceResponse'
import {
  downloadFiles,
  downloadTemplate,
  fetchDocumentObjectionsDescriptions,
  fetchObjectionsByDocumentId,
  fetchTemplates
} from '../../../documents/documents.service'
import { FileModel, UploadedFileResume, UploadedFileStates } from './comunicationFiles.model'
import {
  checkFilesState,
  fetchFilesListItems,
  filterApplicableActionsConfiguration,
  uploadCommunicationFiles
} from './comunicationFiles.service'

import { Warning } from '@material-ui/icons'
import { useDebounce } from 'use-debounce'
import { ObjectionsDialog } from '../../../../components/shared/objectionsDialog/objectionsDialog'
import { AllowedFileTypes } from '../../../../config/allowedFileTypes'
import { usePageState } from '../../../../hooks'
import { mapCommunicationFileToDownloadRequest } from '../../../documents/documents.mapper'
import { ObjectionTableModel } from '../../../invoicesPage/invoicesPage.Model'

export interface ComunicationFilesPagePageProps {
  onNotificationChange: (notificacion: GlobalNotification | undefined) => void
  onFetchingChange: (boolean: boolean) => void
  dateRange: DatePeriod
  customFilter: any
}

export const ComunicationFilesPage: React.FC<ComunicationFilesPagePageProps> = ({
  onNotificationChange,
  onFetchingChange,
  dateRange
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'comunicationFiles', 'enums', 'storageHelper'])

  const { error, awaited } = useNotify()
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const { showError, clearError, errorProps } = useHttpError()

  const { dateFilter, customFilter, activeTab } = usePageState()

  const activeCompany = useSelector(
    (state: RootStoreModel) => state.profile?.activeCompany && state.profile?.activeCompany
  )

  const [globalNotification, setGlobalNotification] = React.useState<
    GlobalNotification | undefined
  >(undefined)

  const [listFiles, setListFiles] = useState<any[] | undefined>(undefined)
  const [listFilesDataTable, setListFilesDataTable] = useState<FileModel[] | undefined>(undefined)
  const [listTemplates, setListTemplates] = useState<TemplateFileModel[] | undefined>(undefined)
  const [isFetching, setIsFetching] = useState<boolean>(true)

  const [key, setKey] = useState(0)
  const [debounceKey] = useDebounce(key, 1000)

  const [openObjectionsDialog, setOpenObjectionsDialog] = useState<boolean>(false)
  const [documentObjections, setDocumentObjections] = useState<ObjectionTableModel[] | undefined>(
    undefined
  )
  const [documentObjectionsDescription, setDocumentObjectionsDescription] = useState<
    ObjectionTableModel[] | undefined
  >(undefined)

  const [uploadedFilesResults, setUploadedFilesResults] = useState<
    UploadedFileResume[] | undefined
  >(undefined)

  const [showUploadedFilesResumeDialog, setShowUploadedFilesResumeDialog] = useState<boolean>(false)

  useEffect(() => {
    const getTemplates = async () => {
      const response = await fetchTemplates()
      if (response.success) setListTemplates(response.data)
    }

    getTemplates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    onNotificationChange(undefined)

    const getFiles = async () => {
      const files = await fetchFiles({
        ...dateRange,
        dateFilter: dateFilter?.activeDateFilter,
        customFilter: customFilter
      })
      setListFilesDataTable(files)
    }

    getFiles()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, dateFilter, customFilter, activeCompany])

  useEffect(() => {
    onFetchingChange(isFetching)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching])

  useEffect(() => {
    uploadedFilesResults !== undefined && setShowUploadedFilesResumeDialog(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFilesResults])

  useEffect(() => {
    onNotificationChange(globalNotification)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalNotification])

  useEffect(() => {
    fetchDocumentObjectionsDescriptions().then(response => {
      if (response.success) {
        setDocumentObjectionsDescription(response.data)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [startRetriesTimer, stopRetriesTimer, isRetriesTimerActive] = useRetries(
    async (pendingFiles: FileModel[], countFilesUpload: number) => {
      pendingFiles = await monitorFiles(pendingFiles, countFilesUpload)

      if (pendingFiles?.length === 0) {
        stopRetriesTimer()
        setGlobalNotification(undefined)
        return
      }
    },
    5000,
    {
      immediate: true,
      onRetry: count => {
        setGlobalNotification({
          message: t('comunicationFiles:globalNotifications.retries.messages.onRetry'),
          actions: [
            {
              label: t('comunicationFiles:globalNotifications.retries.actions.cancel'),
              onClick: () => {
                stopRetriesTimer()
                setGlobalNotification({
                  message: t('comunicationFiles:globalNotifications.retries.messages.onStop'),
                  actions: [
                    {
                      label: t('comunicationFiles:globalNotifications.retries.actions.refresh'),
                      onClick: async () => {
                        setGlobalNotification(undefined)
                        setListFilesDataTable(await fetchFiles(dateRange))
                      }
                    }
                  ]
                })
              }
            }
          ]
        })
      },
      onFinishRetries: () => {
        setGlobalNotification({
          message: t('comunicationFiles:globalNotifications.retries.messages.onFinish'),
          actions: [
            {
              label: t('comunicationFiles:globalNotifications.retries.actions.refresh'),
              onClick: async () => {
                setGlobalNotification(undefined)
                setListFilesDataTable(await fetchFiles(dateRange))
              }
            },
            {
              label: t('comunicationFiles:globalNotifications.retries.actions.close'),
              onClick: () => {
                setGlobalNotification(undefined)
              }
            }
          ]
        })
      },
      onError: error => {
        setGlobalNotification({
          message: t('comunicationFiles:globalNotifications.retries.messages.onError'),
          actions: [
            {
              label: t('comunicationFiles:globalNotifications.retries.actions.refresh'),
              onClick: async () => {
                setGlobalNotification(undefined)
                setListFilesDataTable(await fetchFiles(dateRange))
              }
            }
          ]
        })
      }
    }
  )

  const monitorFiles = async (pendingFiles: FileModel[], targetDataSetCount: number) => {
    let currentFiles = listFilesDataTable
    let dataSetComplete: boolean = false

    if (currentFiles && targetDataSetCount === currentFiles.length) {
      dataSetComplete = await fetchFilesListItems(dateRange).then(response => {
        if (response.success) {
          if (response.data.lenth === currentFiles?.length) {
            setListFilesDataTable(
              response.data?.map((item: FileModel) => {
                return completeDataTableActions(item)
              })
            )
            return true
          }

          return false
        } else {
          return false
        }
      })
    }

    if (dataSetComplete) {
      if (currentFiles && currentFiles.length > 0) {
        return await checkFilesState(
          pendingFiles?.map(dispatch => dispatch.id),
          [
            EnumEstadoFichero.Finalizado,
            EnumEstadoFichero.Error,
            EnumEstadoFichero.Aviso,
            EnumEstadoFichero.StorageException
          ]
        ).then(changedFiles => {
          changedFiles.forEach(changedFile => {
            if (currentFiles) {
              const index = currentFiles.findIndex(x => x.id === changedFile.id)

              currentFiles = [
                ...currentFiles!.slice(0, index),
                completeDataTableActions({ ...changedFile }),
                ...currentFiles!.slice(index + 1)
              ]
              setListFilesDataTable(currentFiles)

              const pendingIndex = pendingFiles.findIndex(x => x.id === changedFile.id)

              pendingFiles = [
                ...pendingFiles!.slice(0, pendingIndex),
                ...pendingFiles!.slice(pendingIndex + 1)
              ]
            }
          })
          return pendingFiles
        })
      } else {
        return []
      }
    } else {
      return pendingFiles
    }
  }

  const completeDataTableActions = (item: FileModel): FileModel => {
    return {
      ...item,
      actions:
        item.actionsConfiguration &&
        item.actionsConfiguration.length > 0 &&
        generateIconsFromConfiguration(
          item,
          filterApplicableActionsConfiguration(item, item.actionsConfiguration),
          new Map<ActionIcons, Function>([])
        ),
      objectionsActions:
        item.objectionsActionsConfiguration &&
        item.objectionsActionsConfiguration.length > 0 &&
        generateIconsFromConfiguration(
          item,
          filterApplicableActionsConfiguration(item, item.objectionsActionsConfiguration),
          new Map<ActionIcons, Function>([])
        )
    }
  }

  const fetchFiles = async (dateRange: DatePeriod): Promise<FileModel[] | undefined> => {
    setIsFetching(true)
    setListFiles(undefined)

    return await fetchFilesListItems(dateRange).then(response => {
      setIsFetching(false)
      if (response.success) {
        clearError()
        if (response.data !== undefined) {
          return response.data?.map((item: FileModel) => {
            return completeDataTableActions(item)
          })
        } else {
          return []
        }
      } else {
        response.error &&
          showError({
            errorCode: response.error.code,
            onRefresh: async () => {
              setListFiles(await fetchFiles(dateRange))
            }
          })
        return []
      }
    })
  }

  function generateListTemplates() {
    let listItems

    if (listTemplates && listTemplates.length > 0) {
      listItems = listTemplates.map((template, index) => (
        <ListItem key={index}>
          <ListItemAvatar>
            <Avatar>
              <DescriptionIcon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={template.primaryText}
            secondary={template.secondaryText}
            secondaryTypographyProps={{ style: { wordWrap: 'break-word' } }}
          />
          <ListItemSecondaryAction>
            {hasPermission(Resources.ComunicationsCenterFiles, [
              UserPermission.READ_ONLY,
              UserPermission.EDIT
            ]) ? (
              <IconButton
                edge="end"
                aria-label="delete"
                onClick={async () => {
                  const response = await downloadTemplate(template.name)
                  if (response.success && response.data)
                    fileDownloadHelper(response.data.name, response.data)
                }}
              >
                <CloudDownloadIcon />
              </IconButton>
            ) : null}
          </ListItemSecondaryAction>
        </ListItem>
      ))
    }

    return listItems
  }

  // -------------------- Files DataTable Handlers -------------------- //
  const onClickObjectionsActionHandler = async (row: FileModel) => {
    setOpenObjectionsDialog(true)
    await fetchObjectionsByDocumentId(row.id!).then(response => {
      if (response.success) {
        setDocumentObjections(response.data)
      }
    })
  }

  const dataTableColumns: DataTableColumn[] = [
    {
      name: 'objectionsActions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML
    },
    {
      name: 'id',
      display: false
    },
    {
      name: 'dischargedDate',
      label: t('comunicationFiles:dataTable.columns.registrationDate'),
      type: DataType.TEXT
    },
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML
    },
    {
      name: 'documentType',
      label: t('comunicationFiles:dataTable.columns.fileType'),
      type: DataType.TEXT
    },
    {
      name: 'description',
      label: t('comunicationFiles:dataTable.columns.fileName'),
      type: DataType.TEXT
    },
    {
      name: 'documentDate',
      label: t('comunicationFiles:dataTable.columns.initDate'),
      type: DataType.DATE
    },
    {
      name: 'endDate',
      label: t('comunicationFiles:dataTable.columns.endDate'),
      type: DataType.DATE
    },
    {
      name: 'state',
      label: t('comunicationFiles:dataTable.columns.state'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumEstadoFichero, 'enums:EnumEstadoFichero')
    }
  ]

  const dataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 1,
    filterType: FilterType.ADVANCED,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    selectableRows: false,
    customInsertEditModal: true,
    analytics: {
      customer: getCustomerDomain(),
      view: t('comunicationFiles:dataTable.analytics')
    },
    rowCustomActions: [
      {
        icon: <CloudDownloadIcon fontSize="small" />,
        tooltip: t('comunicationFiles:dataTable.tooltips.downloadOk'),
        onClick: async (row: FileModel) => {
          if (row.id) {
            const response = await downloadFiles(mapCommunicationFileToDownloadRequest(row.id))
            if (response.success && response.data) {
              fileDownloadHelper(response.data.name, response.data)
            } else {
              error(response.error?.message ?? t('storageHelper:notifications.error.download'))
            }
          }
        }
        //visibleIf: 'isFileOk'
        //userPermission: UserPermission.READ_ONLY
      },
      {
        icon: <ErrorOutlineIcon fontSize="small" />,
        tooltip: t('comunicationFiles:dataTable.tooltips.downloadError'),
        onClick: async (row: FileModel) => {
          if (row.id) {
            const response = await downloadFiles(
              mapCommunicationFileToDownloadRequest(row.id, true)
            )
            if (response.success && response.data) {
              fileDownloadHelper(response.data.name, response.data)
            } else {
              error(response.error?.message ?? t('storageHelper:notifications.error.download'))
            }
          }
        },
        visibleIf: 'isErrorLog'
        //userPermission: UserPermission.READ_ONLY
      },
      {
        icon: <Warning fontSize="small" />,
        tooltip: t('comunicationFiles:dataTable.tooltips.objections'),
        onClick: onClickObjectionsActionHandler,
        userPermission: UserPermission.EDIT
      }
    ]
  }

  const manageFiles = async () => {
    if (listFiles) {
      const files = listFiles.map(file => ({ file: file, name: file.name }))

      // Nota: No mostrar notificaciones de "subida en proceso" antes de validar
      // Algunos archivos pueden ser rechazados por validación de seguridad

      uploadCommunicationFiles(files).then(async (response: ServiceResponse<any>) => {
        if (response.success) {
          // Mostrar notificaciones solo para archivos que se subieron exitosamente
          const successfulUploads = response.data.filter(
            (file: UploadedFileResume) => file.state !== UploadedFileStates.ERROR
          )

          if (successfulUploads.length > 0) {
            awaited(
              successfulUploads.map((file: UploadedFileResume) => {
                return {
                  title: t('comunicationFiles:notifications.uploadFiles.title', {}),
                  message: t('comunicationFiles:notifications.uploadFiles.message'),
                  keyInfo: file.fileName
                } as AwaitedNotification
              })
            )
          }

          setUploadedFilesResults(response.data)

          if (listFilesDataTable) {
            isRetriesTimerActive && stopRetriesTimer()
            startRetriesTimer(
              listFilesDataTable?.filter(
                dispatch =>
                  EnumEstadoFichero.Finalizado !== dispatch.state &&
                  EnumEstadoFichero.Aviso !== dispatch.state &&
                  EnumEstadoFichero.Error !== dispatch.state &&
                  EnumEstadoFichero.StorageException !== dispatch.state
              ),
              response.data.length + listFilesDataTable?.length
            )
          }
        } else {
          error(response.error?.message ?? t('storageHelper:notifications.error.upload'))
        }
      })
      setListFiles([])
      setKey(1 + Math.random() * (1000 - 1))
    }
  }
  // -------------------- Objections Actions -------------------- //
  const handleObjectionsDialogClose = () => {
    setOpenObjectionsDialog(false)
    setDocumentObjections(undefined)
  }

  return (
    <>
      <Grid container spacing={3} alignItems="stretch">
        {hasPermission(Resources.ComunicationsCenterFiles, [UserPermission.EDIT]) ? (
          <Grid item xs={12} sm={6}>
            <Card className={classes.cardHeight}>
              <CardHeader title={t('comunicationFiles:forms.upload.title')} />
              <CardContent>
                <DropzoneArea
                  key={debounceKey}
                  //Icon={React.createElement(CloudDownloadIcon, { className: 'secondary' })}
                  acceptedFiles={[
                    'text/xml',
                    '.xlsx',
                    '.xlsm',
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    '.csv'
                  ]}
                  filesLimit={5}
                  dropzoneClass={classes.dropZone}
                  showAlerts={false}
                  showPreviews={true}
                  showPreviewsInDropzone={false}
                  useChipsForPreview
                  previewGridProps={{ container: { spacing: 1, direction: 'row' } }}
                  previewChipProps={{ classes: { root: classes.previewChip } }}
                  previewText={t('comunicationFiles:forms.upload.selectedFiles')}
                  dropzoneText={t('comunicationFiles:forms.upload.dropText', { maxCount: 5 })}
                  onChange={files => {
                    setListFiles(files)
                  }}
                  initialFiles={listFiles}
                  maxFileSize={AllowedFileTypes.communicationFiles.maxSizeMB * 1024 * 1024}
                />
              </CardContent>

              <CardActions>
                <Button
                  size="small"
                  color="primary"
                  disabled={!listFiles || listFiles?.length === 0}
                  onClick={async () => {
                    manageFiles()
                  }}
                >
                  {t('comunicationFiles:forms.upload.btnUpload')}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ) : null}
        <Grid
          item
          xs={12}
          sm={!hasPermission(Resources.ComunicationsCenterFiles, [UserPermission.EDIT]) ? 12 : 6}
        >
          <Card className={classes.cardHeight}>
            <CardHeader title={t('comunicationFiles:forms.templates.title')} />
            <CardContent style={{ maxHeight: 250, minHeight: 250, overflowY: 'auto' }}>
              {listTemplates ? (
                <List dense={true}>{listTemplates && generateListTemplates()}</List>
              ) : (
                <FormSkeleton elevation={0} columns={1} inputs={2}></FormSkeleton>
              )}
            </CardContent>
            <CardActions></CardActions>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <DataTable
            title={t('comunicationFiles:title')}
            data={listFilesDataTable}
            columns={dataTableColumns}
            options={{
              ...dataTableOptions,
              analytics: {
                customer: getCustomerDomain(),
                view: t('comunicationFiles:dataTable.analytics')
              }
            }}
            //userPermissions={getResourceUserPermissions(Resources.ComunicationsCenterFiles)}
            info={DataTableErrorHandler(errorProps)}
          />
        </Grid>
      </Grid>

      <Dialog open={showUploadedFilesResumeDialog} maxWidth="md" fullWidth={true}>
        <DialogTitle>{t('comunicationFiles:dialogs.uploadedFiles.title')}</DialogTitle>
        <DialogContent dividers>
          <List dense={true}>
            {uploadedFilesResults &&
              uploadedFilesResults.map(file => {
                return (
                  <ListItem key={generateUniqueKeyWithNoPrefix()}>
                    <ListItemIcon>
                      <DescriptionIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${file.fileName}                           `}
                    ></ListItemText>
                    <ListItemSecondaryAction>
                      <Chip
                        label={t(
                          `enums:UploadedFileStates.${enumKeyToValue(
                            UploadedFileStates,
                            file.state
                          )}`
                        )}
                        color={file.state === UploadedFileStates.QUEUED ? 'primary' : 'default'}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                )
              })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowUploadedFilesResumeDialog(false)} color="primary">
            {t('comunicationFiles:dialogs.uploadedFiles.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <ObjectionsDialog
        open={openObjectionsDialog}
        handleClose={handleObjectionsDialogClose}
        title={t('comunicationFiles:dialogs.objectionsDialog.title')}
        objDescriptions={documentObjectionsDescription ?? []}
        objectionsData={documentObjections}
        tableUserPermissions={getUserPermissions(Pages.ComunicationCentre)}
        // errorProps={errorProps}
        // hasEditPermission={hasPermission(Resources.ComunicationsCenterFiles, [UserPermission.EDIT])}
        // allowInsert
      />
    </>
  )
}
